<script setup lang="ts">
import { toRaw } from 'vue'
import { RadioGroupItem, RadioGroupRoot } from 'reka-ui'
import { Circle } from 'lucide-vue-next'
import { cn } from '@/ui/lib/utils'
import type { Icons } from '@/components/base/BaseIcon.vue'

export interface RadioCardOption {
  title?: string
  id?: number | string
  value?: unknown
  img?: string
  icon?: Icons
  color?: string
  description?: string
  disabled?: boolean
}

type OptionLike = RadioCardOption | string | number

const props = withDefaults(
  defineProps<{
    modelValue: unknown
    options: OptionLike[]
    isOnlyImage?: boolean
    iconAsRadio?: boolean
    hideRadio?: boolean
    variant?: 'default' | 'tile'
    class?: string
  }>(),
  { isOnlyImage: false, iconAsRadio: false, hideRadio: false, variant: 'default', class: '' }
)

const emit = defineEmits<(e: 'update:modelValue', value: OptionLike) => void>()

const isObject = (o: unknown): o is RadioCardOption => typeof o === 'object' && o !== null

const sameOption = (opt: OptionLike, modelValue: unknown) => {
  const rawModel = toRaw(modelValue)
  const rawOpt = toRaw(opt)
  if (rawOpt === rawModel) return true
  if (isObject(rawOpt) && isObject(rawModel)) {
    if (rawOpt.id != null && rawModel.id != null) return rawOpt.id === rawModel.id
    if (rawOpt.value !== undefined && rawModel.value !== undefined) return rawOpt.value === rawModel.value
  }
  return false
}

const selectedIndex = computed(() => props.options.findIndex((opt) => sameOption(opt, props.modelValue)))

const internalValue = computed({
  get: () => (selectedIndex.value >= 0 ? String(selectedIndex.value) : ''),
  set: (idx: string) => emit('update:modelValue', props.options[Number(idx)])
})

const isChecked = (index: number) => internalValue.value !== '' && Number(internalValue.value) === index

const getTitle = (o: OptionLike) => (isObject(o) ? o.title : undefined)
const getDescription = (o: OptionLike) => (isObject(o) ? o.description : undefined)
const getIcon = (o: OptionLike) => (isObject(o) ? o.icon : undefined)
const getImg = (o: OptionLike) => (isObject(o) ? o.img : undefined)
const getColor = (o: OptionLike) => (isObject(o) ? o.color : undefined)
const getDisabled = (o: OptionLike) => (isObject(o) ? !!o.disabled : false)

const wrapperClass = computed(() => props.class)
const isTile = computed(() => props.variant === 'tile')
</script>

<template>
  <RadioGroupRoot v-model="internalValue" :class="cn('grid gap-3', wrapperClass)">
    <RadioGroupItem
      v-for="(option, index) in options"
      :key="isObject(option) && option.id != null ? option.id : index"
      :value="String(index)"
      :disabled="getDisabled(option)"
      as="div"
      :tabindex="getDisabled(option) ? -1 : 0"
      :class="[
        'group relative flex items-start gap-3 rounded-xl border bg-card p-4 text-left outline-none',
        isTile
          ? 'transition-[border-color,box-shadow] duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:transition-none'
          : 'transition-colors',
        'data-[state=checked]:border-primary',
        !isTile && 'data-[state=checked]:bg-primary/5',
        'focus-visible:ring-2 focus-visible:ring-primary/30',
        getDisabled(option)
          ? 'cursor-default opacity-50 bg-muted border-border'
          : isTile
            ? 'cursor-pointer data-[state=unchecked]:hover:border-stone-300 data-[state=unchecked]:hover:shadow-card-hover'
            : 'cursor-pointer hover:border-primary'
      ]"
      @keydown.enter.prevent="!getDisabled(option) && (internalValue = String(index))"
      @keydown.space.prevent="!getDisabled(option) && (internalValue = String(index))"
    >
      <div
        v-if="iconAsRadio"
        class="flex items-center justify-center size-10 rounded-lg bg-muted group-data-[state=checked]:bg-primary"
      >
        <BaseIcon
          v-if="getIcon(option)"
          :name="getIcon(option)!"
          class="aspect-square h-6 min-w-fit text-foreground-secondary group-data-[state=checked]:text-white"
        />
      </div>

      <span
        v-else-if="!hideRadio"
        class="mt-0.5 flex aspect-square size-4 shrink-0 items-center justify-center rounded-full border border-input transition-colors group-data-[state=checked]:border-primary"
      >
        <Circle class="size-2 fill-primary text-primary opacity-0 group-data-[state=checked]:opacity-100" />
      </span>

      <div class="min-w-0 flex-1">
        <slot :name="index" :option="option" :checked="isChecked(index)">
          <div v-if="isOnlyImage && !iconAsRadio" class="flex items-start gap-3">
            <BaseIcon
              v-if="getIcon(option)"
              :name="getIcon(option)!"
              :class="getColor(option) ?? 'text-black'"
              class="aspect-square h-6 min-w-fit"
            />
            <img v-if="getImg(option)" :src="getImg(option)" alt="" class="h-20 w-1/2 max-w-none object-contain" />
          </div>
          <div v-else class="flex items-start gap-3">
            <BaseIcon
              v-if="getIcon(option) && !iconAsRadio"
              :name="getIcon(option)!"
              :class="getColor(option) ?? 'text-black'"
              class="aspect-square h-6 min-w-fit"
            />
            <img
              v-if="getImg(option) && !iconAsRadio"
              :src="getImg(option)"
              alt=""
              class="h-6 w-6 max-w-none object-cover"
            />
            <div class="flex min-w-0 flex-col gap-1">
              <span v-if="getTitle(option)" class="truncate text-sm font-medium text-foreground">
                {{ getTitle(option) }}
              </span>
              <span v-if="getDescription(option)" class="text-xs text-muted-foreground">
                {{ getDescription(option) }}
              </span>
            </div>
          </div>
        </slot>
      </div>
    </RadioGroupItem>
  </RadioGroupRoot>
</template>
